import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface PatientPrescriptionSummaryProps {
  patientId: string;
}

export function PatientPrescriptionSummary({ patientId }: PatientPrescriptionSummaryProps) {
  const { data: patient, isLoading: patientLoading } = useQuery({
    queryKey: ["patient", patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("id", patientId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Buscar medicamentos de uso contínuo das prescrições
  const { data: continuousMedications } = useQuery({
    queryKey: ["continuous-medications", patientId],
    queryFn: async () => {
      // Buscar prescription_items através de prescriptions do paciente
      const { data: prescriptions, error: prescriptionsError } = await supabase
        .from("prescriptions")
        .select("id")
        .eq("patient_id", patientId);

      if (prescriptionsError) throw prescriptionsError;
      if (!prescriptions || prescriptions.length === 0) return [];

      const prescriptionIds = prescriptions.map((p) => p.id);

      // Buscar itens de medicamentos
      const { data: items, error: itemsError } = await supabase
        .from("prescription_items")
        .select("prescription, id, duration")
        .in("prescription_id", prescriptionIds)
        .eq("item_type", "medication");

      if (itemsError) throw itemsError;
      if (!items || items.length === 0) return [];

      // Agrupar todos os medicamentos primeiro para contar frequência
      const medicationCount: Record<string, { count: number; hasContinuousDuration: boolean }> = {};
      
      items.forEach((item) => {
        if (item.prescription) {
          const isContinuous = item.duration && (
            item.duration.toLowerCase().includes("contínuo") || 
            item.duration.toLowerCase().includes("continuo")
          );
          
          if (!medicationCount[item.prescription]) {
            medicationCount[item.prescription] = {
              count: 0,
              hasContinuousDuration: false,
            };
          }
          
          medicationCount[item.prescription].count += 1;
          if (isContinuous) {
            medicationCount[item.prescription].hasContinuousDuration = true;
          }
        }
      });

      // Converter para array e ordenar
      // Priorizar medicamentos com duration contínuo confirmado, depois por frequência
      const sortedMedications = Object.entries(medicationCount)
        .map(([prescription, data]) => ({ 
          prescription, 
          count: data.count, 
          hasContinuousDuration: data.hasContinuousDuration 
        }))
        .sort((a, b) => {
          // Primeiro: medicamentos com uso contínuo confirmado
          if (a.hasContinuousDuration !== b.hasContinuousDuration) {
            return a.hasContinuousDuration ? -1 : 1;
          }
          // Segundo: por frequência (mais prescrições primeiro)
          return b.count - a.count;
        })
        .slice(0, 5) // Limitar a 5 principais
        .map(({ prescription, count }) => ({ prescription, count })); // Remover flag auxiliar
      
      if (sortedMedications.length === 0) return [];

      return sortedMedications;
    },
  });

  const { data: alerts } = useQuery({
    queryKey: ["patient-alerts", patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("alerts_onboarding")
        .select("*")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
  });

  const { data: aiAnalysis } = useQuery({
    queryKey: ["ai-analysis", patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_global_analysis_cache")
        .select("*")
        .eq("patient_id", patientId)
        .single();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  if (patientLoading) {
    return (
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/20 dark:to-cyan-950/20">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
        </CardContent>
      </Card>
    );
  }

  if (!patient) return null;

  const age = patient.data_nascimento
    ? Math.floor((new Date().getTime() - new Date(patient.data_nascimento).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/20 dark:to-cyan-950/20">
      <CardContent className="pt-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Nome</p>
            <p className="font-semibold">{patient.full_name}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Idade</p>
            <p className="font-semibold">{age ? `${age} anos` : 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Sexo</p>
            <p className="font-semibold">{patient.sexo || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Data de Nascimento</p>
            <p className="font-semibold">
              {patient.data_nascimento ? format(new Date(patient.data_nascimento), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A'}
            </p>
          </div>
        </div>


        {alerts && alerts.length > 0 && (
          <div>
            <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
              <AlertCircle className="h-4 w-4 text-red-500" />
              Alertas e Contraindicações
            </p>
            <div className="flex flex-wrap gap-2">
              {alerts.map((alert) => (
                <Badge key={alert.id} variant="destructive" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  {alert.title}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {continuousMedications && continuousMedications.length > 0 && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">Medicamentos em Uso</p>
            <div className="flex flex-wrap gap-2">
              {continuousMedications.map((med, index) => (
                <Badge 
                  key={`${med.prescription}-${index}`} 
                  variant="outline" 
                  className="border-cyan-300 text-cyan-700 dark:border-cyan-700 dark:text-cyan-300"
                >
                  {med.prescription}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {aiAnalysis?.json_result && (
          <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-teal-100 to-cyan-100 dark:from-teal-900/20 dark:to-cyan-900/20 border border-teal-200 dark:border-teal-800">
            <p className="text-sm font-medium text-teal-700 dark:text-teal-300 mb-1">💡 Insights da IA</p>
            <p className="text-sm text-muted-foreground">
              {typeof aiAnalysis.json_result === 'object' && aiAnalysis.json_result !== null
                ? (aiAnalysis.json_result as any).summary || 'Análise disponível'
                : 'Análise disponível'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
